import { useCallback } from "react";
import { t } from "@lingui/macro";
import { useDrawerContext, useConfigContext } from "@WahooFitness/wahoo-offline-mfe";
import { openExternalLink } from "./openExternalLink";
import { Plan } from "@WahooFitness/redesignr";
import useTieredMembership, {
  isAppStoreMismatch,
  isWahooApp,
  PROCESSOR,
} from "./useTieredMembership";
import { isSystmSubscription } from "./planCodes";
import useAuthTokenClient from "./useAuthTokenClient";
import {
  buildAppleManagementDrawer,
  buildGoogleManagementDrawer,
  buildSystmManagementDrawer,
  buildWahooManagementDrawer,
  APPLE_MANAGE_URL,
  GOOGLE_MANAGE_URL,
} from "./storeManagementDrawers";
import { capitalize } from "./displayUtils";
import { buildChangePlanUrl } from "./accountsPortalUrl";

export type PurchaseResult = { completed: boolean; error?: string } | null | undefined;

/**
 * Whether a purchase succeeded, for navigation. Native `completed` is only true when RevenueCat
 * surfaced a confirmable transaction, but a cross-grade (especially a deferred downgrade) is
 * scheduled without one and returns `{ completed: false }` with no error. So treat any non-errored
 * result as success — cancellations/failures carry an `error`, and a guarded throw resolves to null.
 */
export function didPurchaseSucceed(result: PurchaseResult): boolean {
  return !!result && !result.error;
}

/**
 * Shared purchase / plan-change action used by both the Change Plan page and the
 * Paywall. Given a target `plan` and the user's current subscription processor it
 * either opens the appropriate "manage on Apple/Google/Wahoo" drawer or performs an
 * in-app purchase via RevenueCat.
 *
 * Selection state, plan visibility, button labels, and analytics are intentionally
 * left to each caller — only the action itself is shared here.
 */
function usePlanPurchase() {
  const {
    activeTier,
    proPkg,
    corePkg,
    makePurchase,
    purchaseInProgress,
    userSubscription,
    hasActiveSub,
  } = useTieredMembership();

  const {
    fitnessAppId,
    cloudConfig: { wahooMyAccountUrl },
  } = useConfigContext();
  const { setDrawer } = useDrawerContext();
  const { getAuthTokens } = useAuthTokenClient();

  const purchasePlan = useCallback(
    async (plan: Plan) => {
      const activeSub = hasActiveSub ? userSubscription : undefined;
      const processorName = activeSub?.subscription_processor?.name;
      const processorUrl = activeSub?.subscription_processor?.url ?? undefined;
      const interval = activeSub?.interval ?? "";
      const activeTierText = capitalize(activeTier);

      if (isSystmSubscription(activeSub) && isWahooApp(Number(fitnessAppId))) {
        const fallbackUrl =
          processorName === PROCESSOR.GOOGLE ? GOOGLE_MANAGE_URL : APPLE_MANAGE_URL;
        const url = processorUrl ?? fallbackUrl;
        setDrawer(
          buildSystmManagementDrawer({
            url,
            subscriptionLabel: t`${activeTierText} ${interval} via SYSTM`,
            activeTierText,
          })
        );
        return;
      }

      const isMismatch = isAppStoreMismatch(Number(fitnessAppId), processorName);

      if (processorName === PROCESSOR.APPLE && isMismatch) {
        const url = processorUrl ?? APPLE_MANAGE_URL;
        setDrawer(
          buildAppleManagementDrawer({
            url,
            subscriptionLabel: t`${activeTierText} ${interval} via Apple`,
            activeTierText,
          })
        );
        return;
      }

      if (processorName === PROCESSOR.GOOGLE && isMismatch) {
        const url = processorUrl ?? GOOGLE_MANAGE_URL;
        setDrawer(
          buildGoogleManagementDrawer({
            url,
            subscriptionLabel: t`${activeTierText} ${interval} via Google Play`,
            activeTierText,
          })
        );
        return;
      }

      if (processorName === PROCESSOR.WAHOO || processorName === PROCESSOR.STRIPE) {
        const { token } = await getAuthTokens();
        const url = buildChangePlanUrl(wahooMyAccountUrl, fitnessAppId, token);
        if (plan === "base") {
          await openExternalLink(url);
          return;
        }
        setDrawer(
          buildWahooManagementDrawer({
            url,
            subscriptionLabel: t`${activeTierText} ${interval} via Wahoofitness.com`,
            activeTierText,
          })
        );
        return;
      }

      // Matching store (apple-on-iOS, google-on-Android) or no existing subscription
      // (non-subscribed paywall user) → in-app purchase. Returns the native purchase
      // result so callers can react to a successful subscription.
      if (!isMismatch) {
        // Base is the free tier — open external cancel flow, same as "Cancel subscription".
        if (plan === "base") {
          if (processorName === PROCESSOR.APPLE) {
            await openExternalLink(processorUrl ?? APPLE_MANAGE_URL);
          } else if (processorName === PROCESSOR.GOOGLE) {
            await openExternalLink(processorUrl ?? GOOGLE_MANAGE_URL);
          }
          return;
        }
        const pkg = plan === "pro" ? proPkg : corePkg;
        // Core while holding active Pro is a deferred downgrade → poll for its webhook-populated
        // pending change. A new Core purchase by a non-subscribed user needs no poll.
        return makePurchase(pkg?.identifier || "", {
          awaitPendingIapChange: plan === "core" && activeTier === "pro",
        });
      }
    },
    [
      userSubscription,
      activeTier,
      hasActiveSub,
      fitnessAppId,
      setDrawer,
      getAuthTokens,
      wahooMyAccountUrl,
      proPkg,
      corePkg,
      makePurchase,
    ]
  );

  return { purchasePlan, purchaseInProgress };
}

export default usePlanPurchase;
